// v10/src/components/common/ToastProvider.js
//
// Sistema centralizado de toasts. Reemplaza a window.alert() para feedback
// rápido (éxito/error/info/warning). Auto-dismiss + portal al body + animación
// con framer-motion. Se consume desde el hook useToast().
//
// Uso básico:
//   const toast = useToast();
//   toast.success('Guardado');
//   toast.error('Falló al guardar');
//
// Uso con action button (agregado 15/5/2026 para el update notification):
//   toast.show('Nueva versión disponible', 'info', null, {
//     label: 'Actualizar',
//     onClick: () => applyUpdate(),
//   });
//   // duration=null → persistente hasta que el user actúe.

import React, { createContext, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

export const ToastContext = createContext(null);

const TYPE_STYLES = {
  success: { color: '#16a34a', bg: 'rgba(22,163,74,0.1)',  border: 'rgba(22,163,74,0.35)',  Icon: CheckCircle },
  error:   { color: '#dc2626', bg: 'rgba(220,38,38,0.1)',  border: 'rgba(220,38,38,0.35)',  Icon: XCircle },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.35)', Icon: AlertTriangle },
  info:    { color: '#2563eb', bg: 'rgba(37,99,235,0.1)',  border: 'rgba(37,99,235,0.35)',  Icon: Info },
};

const DEFAULT_DURATION = 3500;

let toastIdCounter = 0;
const nextId = () => `t${Date.now()}_${++toastIdCounter}`;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // show(message, type, duration, action?)
  //   - message:  string
  //   - type:     'info' | 'success' | 'error' | 'warning'
  //   - duration: ms (null o 0 = persistente, no auto-dismiss)
  //   - action:   { label, onClick } opcional → renderiza un botón
  const show = useCallback((message, type = 'info', duration = DEFAULT_DURATION, action = null) => {
    const id = nextId();
    setToasts((prev) => [...prev, { id, message: String(message ?? ''), type, duration, action }]);
    return id;
  }, []);

  const api = {
    show,
    dismiss,
    success: (msg, duration) => show(msg, 'success', duration),
    error:   (msg, duration) => show(msg, 'error',   duration),
    info:    (msg, duration) => show(msg, 'info',    duration),
    warning: (msg, duration) => show(msg, 'warning', duration),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <ToastContainer toasts={toasts} onDismiss={dismiss} />,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, onDismiss }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        pointerEvents: 'none',
        maxWidth: 'min(420px, calc(100vw - 32px))',
      }}
      aria-live="polite"
      aria-atomic="false"
    >
      <AnimatePresence>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
};

const ToastItem = ({ toast, onDismiss }) => {
  const styles = TYPE_STYLES[toast.type] || TYPE_STYLES.info;
  const Icon = styles.Icon;

  useEffect(() => {
    // Si hay action button, el toast NUNCA auto-dismissea (el usuario debe
    // decidir actuar o cerrarlo manualmente).
    if (toast.action) return undefined;
    if (!toast.duration || toast.duration <= 0) return undefined;
    const id = setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => clearTimeout(id);
  }, [toast.id, toast.duration, toast.action, onDismiss]);

  return (
    <motion.div
      role="status"
      initial={{ opacity: 0, x: 24, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 24, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 14,
        background: '#ffffff',
        border: `1px solid ${styles.border}`,
        boxShadow: '0 12px 36px rgba(15,23,42,0.18)',
        fontFamily: 'inherit',
        fontSize: 13,
        color: '#0f172a',
        minWidth: 220,
      }}
    >
      <div
        style={{
          width: 28, height: 28, borderRadius: 10,
          background: styles.bg, color: styles.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: 16, height: 16 }} />
      </div>
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.45, paddingTop: 4 }}>
        {toast.message}
        {toast.action && (
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={() => {
                try { toast.action.onClick(); } catch (e) { console.error(e); }
                onDismiss(toast.id);
              }}
              style={{
                background: styles.color,
                color: '#ffffff',
                border: 'none',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {toast.action.label}
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Cerrar"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 4, color: '#94a3b8', display: 'flex',
        }}
      >
        <X style={{ width: 14, height: 14 }} />
      </button>
    </motion.div>
  );
};

export default ToastProvider;
